<template>
  <div class="object-select-input clearfix" v-show="show">
    <div class="form-group row">
      <label v-if="!options.noLabel" :class="options.fullWidth ? 'col-sm-12' : 'col-sm-3 col-form-label'">
        {{ options.label }}
        <span v-if="options.required" class="required">*</span>
      </label>
      <div :class="options.fullWidth || options.noLabel ? 'col-sm-12' : 'col-sm-9'">
        <select class="form-control" ref="dropdown"
        :class="formControlValidationClass"
        :name="options.name"
        @change="validate()">
          <template v-if="options.optgroup">
            <template v-for="object in options.objects">
              <optgroup :label="object.text" :key="object">
                <option v-for="val in object.children"
                :key="val.uuid"
                :value="val.uuid"
                :checked="val.uuid === value"
                :title="val[options.titleName]">{{ val[options.objectName] }}</option>
              </optgroup>
            </template>
          </template>
          <template v-else>
            <option v-for="object in options.objects"
            :key="object.uuid"
            :value="object.uuid"
            :checked="object.uuid === value">{{ object[options.objectName] }}</option>
          </template>
        </select>
        <span :class="validationClass" v-show="validationMessage">
          {{ validationMessage }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    ADD_FORM_FIELD,
    REMOVE_FORM_FIELD
  } from '@/vuex/types'
  import Api from '@/vuex/api'
  import Choices from 'choices.js'
  import dom from '@/util/dom'
  import {
    validationClass,
    formControlValidationClass,
    validationMessage
  } from '@/util/helpers'

  export default {
    name: 'ObjectSelectInput',
    props: {
      value: {
        type: String
      },
      options: {
        type: Object,
        default () {
          return {
            label: 'Text',
            name: 'object_select',
            objectName: 'fullName',
            titleName: 'organizationName',
            value: '',
            objects: [],
            required: false,
            noLabel: false
          }
        }
      }
    },
    data () {
      return {
        ignoreSort: false,
        isDirty: false,
        isValid: false,
        choices: false,
        updating: false,
        fieldsAdded: [],
        show: true
      }
    },
    computed: {
      label () {
        return this.options.singleLabel
          ? this.options.singleLabel
          : this.options.label
      },
      validationClass,
      formControlValidationClass,
      validationMessage,
      invalidMessage () {
        return this.options.label + ' is required'
      }
    },
    mounted () {
      if (this.options.hidden) this.show = false
      let $vm = this
      if (this.options.ignoreSort) {
        this.ignoreSort = this.options.ignoreSort
      }
      /* eslint-disable no-new */
      this.choices = new Choices(this.$refs.dropdown, {
        callbackOnCreateTemplates: function (template) {
          let classNames = this.config.classNames
          return {
            item: (data) => {
              let object = $vm.options.objects[data.choiceId - 1]
              let label = data.label
              let closeButton = `
              <button class="${classNames.button}" data-button>Remove item</button>
              `
              if (object && object.icon) {
                label = `<span class="${object.icon} fa-fw"></span> ${label}`
              }
              if (data.title) {
                label = `${label} ${data.title}`
              }
              if (this.config.removeItemButton) {
                label = `${label} ${closeButton}`
              }
              return template(`
              <div class="${classNames.item} ${data.highlighted ? classNames.highlightedState : classNames.itemSelectable}"  data-item data-id="${data.id}" data-value="${data.value}" ${data.active ? 'aria-selected="true"' : ''} ${data.disabled ? 'aria-disabled="true"' : ''}>
                ${label}
              </div>
              `)
            },
            choice: (data) => {
              let object = $vm.options.objects[data.id - 1]
              let label = data.label
              if (object && object.icon) {
                label = `<span class="fa-fw ${object.icon}"></span> ${label}`
              }
              return template(`
              <div class="${classNames.item} ${classNames.itemChoice} ${data.disabled ? classNames.itemDisabled : classNames.itemSelectable}" data-select-text="${this.config.itemSelectText}" data-choice ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'} data-id="${data.id}" data-value="${data.value}" ${data.groupId > 0 ? 'role="treeitem"' : 'role="option"'}>
                ${label}
              </div>
              `)
            }
          }
        },
        placeholder: true,
        shouldSort: !this.ignoreSort,
        removeItemButton: true,
        itemSelectText: '',
        placeholderValue: this.label ? 'Select a ' + this.label : 'Select an item',
        searchPlaceholderValue: this.label ? 'Select a ' + this.label : 'Select an item',
        fuseOptions: {
          threshold: 0.2,
          maxPatternLength: 128,
          matchAllTokens: true,
          tokenize: true
        },
        searchResultLimit: 6,
        searchFields: ['label'],
        callbackOnInit: function () {
          this.containerInner.id = $vm.options.name
          if (!$vm.value) {
            let arr = this.store.getItemsFilteredByActive()
            if (arr.length > 0) {
              let value = arr.pop().value
              this.removeItemsByValue(value)
            }
          } else {
            this.setValueByChoice($vm.value)
            $vm.validate()
          }
        }
      })
      document.removeEventListener('click', this.choices._onClick)
      document.removeEventListener('keyup', this.choices._onKeyUp)
      document.removeEventListener('keydown', this.choices._onKeyDown)
      document.addEventListener('click', (e) => {
        if (e.target.className.includes('choices') && e.target.tagName !== 'BUTTON') {
          this.choices._onClick(e)
        }
      }, true)
      document.addEventListener('keydown', (e) => {
        if (document.activeElement.className.includes('choices')) {
          if (e.keyCode === 13) {
            this.choices._onKeyDown(e)
            this.isSearching = false
          } else if (e.target.className.includes('choices')) {
            this.isSearching = true
            this.choices._onKeyDown(e)
          }
        } else {
          this.isSearching = false
        }
      }, true)
      document.addEventListener('keyup', (e) => {
        if (this.isSearching && document.activeElement.className.includes('choices')) {
          this.choices._onKeyUp(e)
        }
      }, true)
    },
    watch: {
      'options.objects': function (oldVal, newVal) {
        this.updateChoices()
      }
    },
    methods: {
      async updateChoices () {
        if (this.choices) {
          // updates the choices input with new values
          this.choices.clearInput()
          this.choices.config.searchPlaceholderValue = 'Select a ' + this.options.label
          this.choices.config.placeholderValue = 'Select a ' + this.options.label
          if (this.choices.containerInner.children[1].children[0]) {
            this.choices.containerInner.children[1].children[0].innerHTML = ''
          }
          this.choices.containerOuter.children[1].children[0].placeholder = 'Select a ' + this.options.label
          this.choices.setChoices(this.options.objects, 'uuid', this.options.objectName, true)
          await this.$nextTick()
          if (this.value) {
            this.choices.setValueByChoice(this.value)
          }
        }
      },
      async getCustomReportTypeFields (val) {
        if (this.fieldsAdded.length) {
          for (let field of this.fieldsAdded) {
            this.$store.commit(REMOVE_FORM_FIELD, {
              permalink: this.options.permalink,
              name: field.name
            })
          }
        }
        let instance = new Api()
        let fields = await instance.getCustomReportTypeFields(val)
        this.fieldsAdded = fields
        for (let field of fields) {
          this.$store.commit(ADD_FORM_FIELD, {
            permalink: this.options.permalink,
            field
          })
        }
      },
      async validate (val) {
        await this.$nextTick()
        let data = false
        if (!val) {
          data = this.choices.getValue()
          val = data ? data.value : null
        }
        this.$emit('input', val)
        if (this.options.appendCustomFields) {
          this.getCustomReportTypeFields(val)
        }
        if (this.options.required) {
          this.isDirty = true
          this.isValid = val && val.length > 0
        } else {
          this.isValid = true
        }
        let choicesInner = document.getElementById(this.options.name)
        if (this.isValid) {
          if (dom(choicesInner).hasClass('is-invalid')) {
            dom(choicesInner).removeClass('is-invalid')
          }
          if (!dom(choicesInner).hasClass('is-valid')) {
            dom(choicesInner).addClass('is-valid')
          }
        } else {
          if (dom(choicesInner).hasClass('is-valid')) {
            dom(choicesInner).removeClass('is-valid')
          }
          if (!dom(choicesInner).hasClass('is-invalid')) {
            dom(choicesInner).addClass('is-invalid')
          }
        }
      },
      runWatcher (val, name) {
        switch (name) {
          case 'AccountUuid':
            this.getCars(val)
            break
          case 'address':
            this.geocode(val)
            break
          case 'isCustom':
            this.show = !val
            if (val) {
              this.choices.clearInput()
              this.choices.config.searchPlaceholderValue = 'Select a ' + this.options.label
              this.choices.config.placeholderValue = 'Select a ' + this.options.label
              if (this.choices.containerInner.children[1].children[0]) {
                this.choices.containerInner.children[1].children[0].innerHTML = ''
              }
              this.choices.containerOuter.children[1].children[0].placeholder = 'Select a ' + this.options.label
            }
            break
        }
      },
      async geocode (val) {
        let instance = new Api()
        let addressComponents = await instance.geocode(val)
        if (this.options.name.toLowerCase().includes('state')) {
          this.choices.setValueByChoice(addressComponents.state)
          this.validate()
        }
      },
      async getCars (val) {
        let instance = new Api()
        let results = await instance.getUserCars({
          accountUuid: val
        })
        results.map(function (val, i) {
          results[i] = { value: val.uuid, label: val.dropdownLabel }
        })
        if (this.choices) {
          if (this.choices.getValue()) {
            let found = results.find(el => el.value === this.choices.getValue().value)
            if (!found) {
              this.choices.clearInput()
              if (this.choices.containerInner.children[1].children[0]) {
                this.choices.containerInner.children[1].children[0].innerHTML = ''
              }
            }
          }
          this.choices.setChoices(results, 'value', 'label', true)
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/inputs/ObjectSelectInput.vue